import type { PersistedSnapshotV1 } from "../persistence/records";
import { CURRENT_BACKUP_FORMAT_VERSION } from "./backup-document";

/**
 * Backup format versions are independent from Dexie schema versions. Add a pure
 * migration step here before changing CURRENT_BACKUP_FORMAT_VERSION.
 */
export function migrateBackupPayload(version: number, payload: unknown): PersistedSnapshotV1 {
  if (version === CURRENT_BACKUP_FORMAT_VERSION) return payload as PersistedSnapshotV1;
  if (version === 3) {
    return addFoodMeasureDefaults(requireLegacyPayload(payload));
  }
  if (version === 1 || version === 2) {
    const legacy = requireLegacyPayload(payload);
    return addFoodMeasureDefaults({
      ...legacy,
      // Some transitional writers emitted later tables before advancing the
      // format number. Preserve them when present while supplying safe defaults.
      onboardingReferences: version === 1
        ? (Array.isArray(legacy.onboardingReferences) ? legacy.onboardingReferences : [])
        : legacy.onboardingReferences,
      recipes: Array.isArray(legacy.recipes) ? legacy.recipes : [],
    });
  }
  throw new Error(`Backup format version ${version} is not supported by this app`);
}

function requireLegacyPayload(payload: unknown): Record<string, unknown> {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Legacy backup payload must be an object");
  }
  return payload as Record<string, unknown>;
}

/**
 * Format 4 introduced optional alternate food measures and diary display
 * measures. Materialize the food-side arrays for older payloads without
 * changing canonical portions or historical nutrition. Invalid legacy values
 * are deliberately preserved so the normal snapshot validator can reject them.
 */
function addFoodMeasureDefaults(payload: Record<string, unknown>): PersistedSnapshotV1 {
  return {
    ...payload,
    foods: withMeasures(payload.foods),
    diaryEntries: withDiaryMeasures(payload.diaryEntries),
  } as unknown as PersistedSnapshotV1;
}

function withMeasures(input: unknown): unknown {
  if (!Array.isArray(input)) return input;
  return input.map((item) => {
    if (!isRecord(item)) return item;
    return item.measures === undefined ? { ...item, measures: [] } : { ...item };
  });
}

function withDiaryMeasures(input: unknown): unknown {
  if (!Array.isArray(input)) return input;
  return input.map((item) => {
    if (!isRecord(item) || !isRecord(item.food)) return item;
    const food = item.food.measures === undefined
      ? { ...item.food, measures: [] }
      : { ...item.food };
    return { ...item, food };
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
